import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, ShieldCheck, Loader2 } from "lucide-react";

export default function BlockedUsers() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("blocked_users_title"));
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/user-blocks"); setItems(Array.isArray(data?.items) ? data.items : []); }
    catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unblock = async (id) => {
    setBusy(id);
    try { await api.delete(`/user-blocks/${id}`); setItems((prev) => (prev || []).filter((b) => b.blocked_id !== id)); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(locale); } catch { return iso; } };

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="blocked-users-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-2xl text-foreground">{t("blocked_users_title")}</h1>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <ShieldCheck className="h-9 w-9 text-muted-foreground/60" />
          <p className="max-w-xs text-sm text-muted-foreground">{t("blocked_users_empty")}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((item) => (
            <div key={item.id} data-testid={`block-row-${item.blocked_id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-secondary/60 p-4">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">{item.blocked_name || item.blocked_id}</p>
                <p className="text-xs text-muted-foreground">{t("blocked_at")}: {fmt(item.created_at)}</p>
                {item.reason ? <p className="mt-0.5 text-xs italic text-muted-foreground">{String(item.reason)}</p> : null}
              </div>
              <button data-testid={`unblock-${item.blocked_id}`} onClick={() => unblock(item.blocked_id)} disabled={busy === item.blocked_id}
                className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">{t("unblock_cta")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
